<!-- eslint-disable vue/v-on-event-hyphenation -->
<template>
  <div
    v-if="
      surveyDetails && (!shouldFindSuitableSurvey || suitableSurveyLookedUp)
    "
    :class="resultViewHasFeelgoodWrapper && 'pb-20'"
  >
    <TopBanner v-if="topBannerEnabled" :surveyName="surveyName" class="mt-5" />

    <Notebook
      :showNotes="showNotes"
      :surveyName="surveyName"
      :organizationName="organizationName"
      :username="userInfo.username"
      :teamForSelectedGroup="teamForSelectedGroup"
      @notesClosed="handleToggleNotes"
    />

    <div
      class="d-flex align-items-center justify-content-between gap-5 mt-5 flex-wrap"
    >
      <UtilityBar
        v-if="selectedSurvey && surveyDetails"
        :disabled="surveyDetailsLoading"
        :hasLoadedAllHistory="historyLoadedUntilYear === allHistoryYear"
        :surveysForSelector="surveysForSelector"
        :groupsForSelector="groupsForSelector"
        :selectedGroup="selectedGroup"
        :attributeSegmentsForSelector="attributeSegmentsForSelector"
        :selectedAttributeSegment="selectedAttributeSegment"
        :selectedTab="selectedTab"
        :selectedSurvey="selectedSurvey"
        :surveyDetails="surveyDetails"
        :selectedTimePeriod="selectedTimePeriod"
        :userIsAdminForCurrentGroup="userIsAdminForCurrentGroup"
        @selectGroup="onSelectGroup"
        @selectSurvey="onSelectSurvey"
        @selectAttributeSegment="onSelectAttributeSegment"
        @setSelectedTimePeriod="setSelectedTimePeriod"
        @loadAllHistoricalSurveys="loadAllHistoricalSurveys"
      />
    </div>

    <SurveyResultTabsNavigation
      class="mt-5"
      :selectedTab="selectedTab"
      :onTabSelected="onTabSelected"
      :userIsAdminForSelectedGroup="selectedGroup.userIsAdmin"
      :userIsPulsAdmin="userStore.userIsPulsAdmin"
      :userIsGroupAdmin="userStore.userIsGroupAdmin"
      :shouldShowNotebook="shouldShowNotebook"
      :surveyContainsOnlyFreeTextQuestions="surveyContainsOnlyFreeTextQuestions"
      @changeViewMode="handleChangeViewMode"
      @toggleNotes="handleToggleNotes"
      @goToResults="goToResults"
    />

    <div v-if="error" class="text-danger mt-5">
      {{ error }}
    </div>
    <component
      :is="currentTabComponent"
      v-if="hasInitializedSurveyDetails"
      v-bind="propsForComponent"
      @proposal-selected="handleProposalSelected"
    />
    <div v-else-if="surveyDetailsLoading || surveysLoading">
      <LoadingSpinner :active="true" spinner="bar-fade-scale" class="pt-20" />
    </div>
    <ResultsMessages
      v-if="shouldShowResultsMessages"
      ref="resultsMessages"
      :actions="actionsStore.actions && actionsStore.actions.actions"
      :surveyDetails="surveyDetails.details"
      :surveyName="surveyName"
      :organizationFullName="organizationFullName"
      :organizationName="organizationName"
      :selectedGroup="selectedGroup"
      :selectedAttributeSegment="selectedAttributeSegment"
      :categories="resultsStore.categories"
      :questions="resultsStore.questions"
      :tags="resultsStore.tags"
      :trendCategories="resultsStore.trendCategories"
      :trendTags="resultsStore.trendTags"
      :trendQuestions="resultsStore.trendQuestions"
      :section="sectionForResultsMessages"
      :debug="debug"
      @proposal-selected="handleProposalSelected"
    />
  </div>
  <div v-else>
    <div v-if="surveyDetailsLoading || surveysLoading">
      <LoadingSpinner :active="true" spinner="bar-fade-scale" class="pt-20" />
    </div>
    <div v-else>
      <div>
        <div class="alert alert-warning mt-5" role="alert">
          <h4 v-if="error">
            {{ $t(`errors.status.${error.message}`) }}
          </h4>

          <p class="mt-5">
            <router-link :to="{ name: 'Results' }">
              {{ $t('page.results') }}
            </router-link>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DataExporter from '@/components/export/DataExporter.vue'
// import AttributeSegmentSelector from '@/components/results/subcomponents/AttributeSegmentSelector.vue'
// import GroupSelector from '@/components/results/subcomponents/GroupSelector.vue'
import TopBanner from '@/components/TopBanner.vue'
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue'
import FreeTextVisualizer from '@/components/freetext/FreeTextVisualizer.vue'
import Comparison from '@/components/comparison/Comparison.vue'
import Notebook from '@/components/notes/Notebook.vue'
import ResultsMessages from '@/components/realtime/ResultsMessages.vue'
import SurveyResultTabsNavigation from '@/components/result-tabs/SurveyResultTabsNavigation.vue'
import WidgetGrid from '@/components/results/subcomponents/WidgetGrid.vue'
import UtilityBar from './subcomponents/UtilityBar.vue'
import {
  makeDataRequest,
  storeDataKeyToRequestTypeMap,
  requestNameToStoreMap
} from '@/stores/data-loader'
import { useActionsStore } from '@/stores/actions-store'
import { useComparisonStore } from '@/stores/comparison-store'
import { useNotesStore } from '@/stores/notes-store'
import { useInsightsStore } from '@/stores/insights-store'
import { useRealtimeStore } from '@/stores/realtime-store'
import { useResponsesStore } from '@/stores/responses-store'
import { useResultsStore } from '@/stores/results-store'
import { useStatesStore } from '@/stores/states-store'
import { useSurveysStore } from '@/stores/surveys-store'
import { useTeamsStore } from '@/stores/teams-store'
import { useUserStore } from '@/stores/user-store'
import { useWidgetStore } from '@/stores/widget-store'
import {
  findFirstTimePeriodWithData,
  timeObjectFromSelectedTimePeriod,
  subtractTimeObjectFromSurveyDate
} from '@/helpers/time-window-helper'
import { timePeriodsWithLabels, allHistoryYear } from '@/constants/time-types'
import { determineGroupToLoadFirst } from '@/helpers/survey-helpers'
import { requestTypes } from '@/constants/request-types.js'
import { tabTypes } from '@/constants/tab-types'
export default {
  name: 'SurveyResults',
  components: {
    // AttributeSegmentSelector,
    // GroupSelector,
    Comparison,
    DataExporter,
    LoadingSpinner,
    Notebook,
    ResultsMessages,
    SurveyResultTabsNavigation,
    WidgetGrid,
    TopBanner,
    UtilityBar
  },
  setup() {
    const actionsStore = useActionsStore()
    const comparisonStore = useComparisonStore()
    const notesStore = useNotesStore()
    const insightsStore = useInsightsStore()
    const realtimeStore = useRealtimeStore()
    const responsesStore = useResponsesStore()
    const resultsStore = useResultsStore()
    const statesStore = useStatesStore()
    const surveysStore = useSurveysStore()
    const teamsStore = useTeamsStore()
    const userStore = useUserStore()
    const widgetStore = useWidgetStore()

    // const timePeriodOptions = timePeriodsWithLabels(this.$t)

    return {
      actionsStore,
      comparisonStore,
      notesStore,
      insightsStore,
      realtimeStore,
      responsesStore,
      resultsStore,
      statesStore,
      surveysStore,
      teamsStore,
      userStore,
      widgetStore,
      allHistoryYear
      // timePeriodOptions
    }
  },
  data() {
    return {
      hasInitializedSurveyDetails: false,
      hasLoadedOlderSurveys: false,
      requestCallbacks: {
        updateResponses: this.updateResponses
      },
      suitableSurveyLookedUp: false,
      showNotes: false,
      selectedTab: tabTypes.RESULTS,
      timePeriodOptions: timePeriodsWithLabels(this.$t),
      doneWithAllWidgets: false
    }
  },
  computed: {
    currentTabComponent() {
      // Map the selected tab to its component name.
      switch (this.selectedTab) {
        case tabTypes.RESULTS:
          return WidgetGrid
        case tabTypes.FREETEXT:
          return FreeTextVisualizer
        case tabTypes.COMPARISON:
          return Comparison
        case tabTypes.EXPORT:
          return DataExporter
        default:
          return null
      }
    },
    historyLoadedUntilYear() {
      return this.surveysStore.historyLoadedUntilYear
    },
    propsForComponent() {
      const statePropsSharedByAll = {
        selectedAttributeSegment: this.selectedAttributeSegment,
        selectedGroup: this.selectedGroup,
        surveyName: this.surveyName,
        surveyIsAggregate: this.surveyIsAggregate,
        surveyIsAggregateV2: this.surveyIsAggregateV2,
        surveyDetails: this.surveyDetails,
        filteredSurveyDetails: this.filteredSurveyDetails,
        theme: this.theme,
        shouldShowResultsMessages: this.shouldShowResultsMessages,
        surveyContainsOnlyFreeTextQuestions:
          this.surveyContainsOnlyFreeTextQuestions
      }
      const orgNameProps = {
        organizationName: this.organizationName,
        organizationFullName: this.organizationFullName
      }
      const widgetHandlingProps = {
        deregisteredWidgets: this.deregisteredWidgets,
        getDataForRequestFromStoresCallback: this.getDataForRequestFromStores,
        onChangeViewMode: this.handleChangeViewMode,
        onComponentError: this.handleComponentError,
        onWidgetDeregistration: this.handleWidgetDeregistration,
        onWidgetRegistration: this.handleWidgetRegistration,
        updateWidgetSliderState: this.updateWidgetSliderState,
        updateWidgetRegistration: this.updateWidgetRegistration,
        requestCallbacks: this.requestCallbacks,
        widgetRegistrations: this.widgetRegistrations,
        widgetSliderStates: this.widgetSliderStates
      }
      const adminProps = {
        userIsGroupAdmin: this.userStore.userIsGroupAdmin,
        userIsPulsAdmin: this.userStore.userIsPulsAdmin
      }

      switch (this.selectedTab) {
        case tabTypes.RESULTS:
          return {
            ...statePropsSharedByAll,
            ...orgNameProps,
            ...widgetHandlingProps,
            ...adminProps,
            resultViewForUser: this.resultViewForUser,
            userInfo: this.userInfo,
            viewMode: this.viewMode,
            hasResults: this.hasResults
          }
        case tabTypes.FREETEXT:
          return { ...statePropsSharedByAll }
        case tabTypes.COMPARISON:
          return {
            ...statePropsSharedByAll,
            ...orgNameProps,
            ...adminProps,
            userInfo: this.userInfo,
            attributes: this.attributeSegmentsForSelector,
            organizationGroup: this.organizationGroup,
            onTabSelected: this.onTabSelected,
            onSelectGroup: this.onSelectGroup,
            onSelectAttributeSegment: this.onSelectAttributeSegment,
            groups: this.groups,
            selectedTimePeriod: this.selectedTimePeriod,
            makeDataRequest: makeDataRequest
          }
        case tabTypes.EXPORT:
          return {
            ...statePropsSharedByAll,
            organizationGroup: this.organizationGroup,
            groups: this.groupsForSelector,
            resultViewForUser: this.resultViewForUser
          }
        default:
          return null
      }
    },
    sectionForResultsMessages() {
      switch (this.selectedTab) {
        case tabTypes.RESULTS:
          return 'overview'
        case tabTypes.FREETEXT:
          return 'overview'
        case tabTypes.COMPARISON:
          if (this.selectedAttributeSegment.name === 'all') {
            return 'groupsComparison'
          }
          return null
        case tabTypes.EXPORT:
          return null
        default:
          return null
      }
    },
    debug() {
      return !!this.$route.query.debug
    },
    attributesForResults() {
      return this.statesStore.getAttributesForResults
    },
    attributeSegmentsForSelector() {
      // return [
      //   {
      //     name: 'custom:workforce-LT',
      //     attributeName: 'custom:workforce',
      //     segmentName: 'LT',
      //     title: 'Workforce-LT',
      //     segmentTitle: 'lt'
      //   },
      //   {
      //     name: 'custom:workforce-NLT',
      //     attributeName: 'custom:workforce',
      //     segmentName: 'LT',
      //     title: 'Workforce-NLT',
      //     segmentTitle: 'nlt'
      //   }
      // ]
      const enabledAttributesForResults = this.attributesForResults
        .filter((a) => a.data.segments?.length) // a.enabled is only for hiding/showing possibility to select segment for user
        .reduce((res, attribute) => {
          res.push({
            name: `${attribute.name}`,
            title: this.$tr(attribute.data.messages, 'title'),
            level: 0,
            $isDisabled: true
          })
          attribute.data.segments.forEach((segmentName) => {
            res.push({
              name: `${attribute.name}-${segmentName}`,
              attributeName: attribute.name,
              segmentName: segmentName,
              title: this.$tr(
                attribute.data.messages,
                `segments.${segmentName}`
              ),
              segmentTitle: this.$tr(
                attribute.data.messages,
                `segments.${segmentName}`
              ),
              attributeTitle: this.$tr(attribute.data.messages, 'title'),
              level: 1
            })
          })
          return res
        }, [])
      // TODO: find a better solution to keep it DRY
      if (enabledAttributesForResults.length) {
        return [
          {
            name: 'all',
            title: this.$t('resultsPerAttribute.attributes.all.title')
          }
        ].concat(enabledAttributesForResults)
      }
      return []
    },
    connectionId() {
      return this.realtimeStore.connectionId
    },
    error() {
      if (this.surveysStore.error[requestTypes.SURVEY_DETAILS]) {
        return this.surveysStore.error[requestTypes.SURVEY_DETAILS]
      } else if (Object.keys(this.resultsStore.error).length) {
        return this.resultsStore.error[Object.keys(this.resultsStore.error)[0]]
      } else {
        return null
      }
    },
    groups() {
      const groups = this.surveysStore.getGroups
      return groups.map((g) => ({ ...g, title: this.$tr(g.messages, 'title') }))
    },
    organizationGroup() {
      return this.statesStore.getOrganizationGroup
    },
    groupsForSelector() {
      const title = this.$tr(this.organizationGroup.messages, 'title')
      const initGroups = !this.shouldExcludeOrganizationStatistics
        ? [
            {
              name: this.organizationGroup.name,
              title,
              messages: {
                en: { title }
              }
            }
          ]
        : []
      return initGroups
        .concat(this.groups)
        .filter((g) => {
          return (
            !g.isOrganizationGroup || !this.shouldExcludeOrganizationStatistics
          )
        })
        .map((g) => {
          return {
            ...g,
            title: `${g.userIsAdmin ? '⭐︎ ' : ''}${g.title} `
          }
        })
    },
    hasResults() {
      if (this.selectedTab === tabTypes.COMPARISON) {
        return (
          Object.keys(
            this.comparisonStore.resultsForSurveys?.[this.surveyName] || {}
          ).length > 0
        )
      } else {
        const categories = this.resultsStore.categories
        if (Object.keys(categories).length) {
          const firstCategory = categories[Object.keys(categories)[0]]
          return firstCategory && firstCategory.numberOfResponses > 0
        }
        return false
      }
    },
    hasInsights() {
      const teamInsights = this.insightsStore.insightsTeam?.insights
      const leadershipInsights = this.insightsStore.insightsLeadership?.insights

      const hasTeamInsights =
        (Array.isArray(teamInsights?.positive) &&
          teamInsights.positive.length > 0) ||
        (Array.isArray(teamInsights?.negative) &&
          teamInsights.negative.length > 0)

      const hasLeadershipInsights =
        (Array.isArray(leadershipInsights?.positive) &&
          leadershipInsights.positive.length > 0) ||
        (Array.isArray(leadershipInsights?.negative) &&
          leadershipInsights.negative.length > 0)

      return hasTeamInsights || hasLeadershipInsights
    },
    organizationName() {
      return this.statesStore.getOrganizationName
    },
    organizationFullName() {
      return this.statesStore.getOrganizationFullName
    },
    realtimeEnabled() {
      return this.realtimeStore.realtimeEnabled
    },
    resultViewTypeForUser() {
      if (this.userStore.userIsPulsAdmin) {
        return 'admin'
      }
      if (this.userIsAdminForCurrentGroup) {
        return 'admin'
      } else {
        return 'user'
      }
    },
    resultViewForUser() {
      return this.surveyDetails.details.resultViews[this.resultViewTypeForUser]
    },
    // used to add extra padding so the bottom sticky bar won't cover other widgets
    resultViewHasFeelgoodWrapper() {
      return this.resultViewForUser.rows.find((row) => {
        return row.cols.find((col) => {
          return col.type === 'wrapper' && col.data.variant === 'friskkollen'
        })
      })
    },
    selectedGroup() {
      return this.statesStore.selectedGroup
    },
    selectedSurvey() {
      return this.statesStore.surveyName
        ? {
            name: this.statesStore.surveyName,
            title: this.$tr(this.surveyDetails?.details.messages, 'title')
          }
        : null
    },
    selectedTimePeriod() {
      return this.statesStore.getSelectedTimePeriod
    },
    selectedAttributeSegment() {
      const selected = this.statesStore.selectedAttributeSegment
      if (selected) {
        return selected
      } else {
        return {
          name: 'all',
          title: this.$t('resultsPerAttribute.attributes.all.title')
        }
      }
    },
    shouldExcludeOrganizationStatistics() {
      return this.surveyDetails.details.config?.excludeOrganizationStatistics
    },
    shouldDisplayTopBanner() {
      return true
    },
    shouldFindSuitableSurvey() {
      return this.$route.query && this.$route.query.load
    },
    shouldShowNotebook() {
      return !this.teamsStore.loading && !!this.teamForSelectedGroup
    },
    shouldShowResultsMessages() {
      const baseCondition =
        this.connectionId &&
        this.realtimeEnabled &&
        this.sectionForResultsMessages
      if (
        this.selectedTab === tabTypes.RESULTS ||
        this.selectedTab === tabTypes.FREETEXT
      ) {
        return baseCondition && this.hasResults && !!this.selectedGroup
      }
      if (this.selectedTab === tabTypes.COMPARISON) {
        return baseCondition && this.hasInsights
      }
      return false
    },
    surveyContainsOnlyFreeTextQuestions() {
      return this.surveyDetails?.details.categories.every((category) =>
        category.questions.every(
          (question) => question.details.type === 'freetext'
        )
      )
    },
    surveyDetails() {
      return this.surveysStore.getSurveyDetails
    },
    filteredSurveyDetails() {
      return this.surveysStore.filteredSurveyDetails
    },
    surveys() {
      return this.surveysStore.getSurveys
    },
    surveyIsAggregate() {
      return this.statesStore.surveyIsAggregate
    },
    surveyIsAggregateV2() {
      return this.statesStore.surveyIsAggregateV2
    },
    surveysForSelector() {
      const otherSurveys = this.surveyDetails?.allSurveysInfo
        .filter((s) => {
          if (this.surveyIsAggregate) {
            return !!s.details.aggregate
          } else {
            return (
              !s.details.aggregate &&
              (s.details.status === 'published' ||
                s.details.status === 'completed')
            )
          }
        })
        .map((s) => ({
          name: s.name,
          title: this.$tr(s.details.messages, 'title'),
          messages: s.details.messages,
          end_date: s.details.end_date
        }))

      const currentSurvey = {
        name: this.surveyDetails.name,
        title: this.surveyTitle,
        messages: this.surveyDetails.details.messages,
        end_date: this.surveyDetails.details.end_date
      }

      return [...otherSurveys, currentSurvey].sort((a, b) => {
        return a.end_date > b.end_date ? -1 : 1
      })
    },
    surveyTitle() {
      return this.$tr(this.surveyDetails.details.messages, 'title')
    },
    surveyName() {
      return this.$route.params.surveyName
    },
    surveyDetailsLoading() {
      return this.surveysStore.loading.surveyDetails
    },
    surveysLoading() {
      return this.surveysStore.loading.surveys
    },
    teamForSelectedGroup() {
      if (this.selectedGroup.name === this.organizationGroup.name) {
        return this.organizationGroup.name
      }
      return this.statesStore.teamForSelectedGroup?.name
    },
    theme() {
      return this.surveyDetails.details.config?.theme
    },
    topBannerEnabled() {
      // return true if date is before 2025-08-30
      const today = new Date()
      const bannerEndDate = new Date('2025-08-30')
      return today < bannerEndDate
    },
    userInfo() {
      return this.userStore.getUserInfo
    },
    userIsAdminForCurrentGroup() {
      if (this.selectedGroup.name !== this.organizationGroup.name) {
        return this.selectedGroup.userIsAdmin
      } else if (this.selectedGroup.name === this.organizationGroup.name) {
        return this.userStore.userIsPulsAdmin
      } else {
        return false
      }
    },
    deregisteredWidgets() {
      return this.widgetStore.getDeregisteredWidgets
    },
    widgetRegistrations() {
      return this.widgetStore.getWidgetRegistrations
    },
    widgetSliderStates() {
      return this.widgetStore.getWidgetSliderStates
    },
    viewMode() {
      return this.statesStore.getViewMode
    }
  },
  watch: {
    widgetRegistrations: {
      deep: true,
      handler() {
        if (
          this.doneWithAllWidgets ||
          this.selectedTab === tabTypes.COMPARISON
        ) {
          this.requestDataForRegisteredWidgets()
        }
      }
    },
    surveyDetails(newDetails, oldDetails) {
      const hasChangedSurvey =
        newDetails && newDetails.name !== oldDetails?.name
      // if we don't have surveys history, load more before initializing
      if (
        newDetails &&
        !newDetails.oldSurveys?.length &&
        !this.hasLoadedOlderSurveys
      ) {
        this.loadAllHistoricalSurveys()
        // if we have loaded more surveys, but still haven't initialized
      } else if (
        this.hasLoadedOlderSurveys &&
        !this.hasInitializedSurveyDetails
      ) {
        this.handleSurveyDetailsLoaded(newDetails)
      } else if (hasChangedSurvey) {
        this.handleSurveyDetailsLoaded(newDetails)
      }

      if (this.surveyContainsOnlyFreeTextQuestions) {
        this.selectedTab = tabTypes.FREETEXT
      }
    },
    surveyName() {
      this.hasInitializedSurveyDetails = false
      // if survey name changes in url, then we load details for it
      this.onSurveyLoad()
    },
    surveys() {
      if (this.shouldFindSuitableSurvey) {
        // in case we're looking for a suitable, we can now check for one
        this.navigateToSuitableSurvey()
      }
    }
  },
  mounted() {
    this.selectedTab = this.$route.name || tabTypes.RESULTS
    this.statesStore.setSelectedGroup(this.organizationGroup)
    this.onSurveyLoad()
  },
  methods: {
    findSuitableSurvey() {
      if (!this.surveys.length) {
        // const surveyDetails = this.surveysStore.getSurveyDetails
        // const currentSurveysOnly = DateTime.fromISO(
        //   surveyDetails.details.start_date
        // ).year
        const currentSurveysOnly = false // unfortunately we need to load all surveys to get older aggregates
        this.surveysStore.loadSurveysAction({ currentSurveysOnly })
      }
    },
    findSuitableTimeWindowAndGroupForCurrentSurvey() {
      const firstPeriod = findFirstTimePeriodWithData({
        surveyDetails: this.surveyDetails,
        timePeriodOptions: this.timePeriodOptions
      })
      this.setSelectedTimePeriod(firstPeriod?.value)
      // check if currently selected group is avaialble for the survey
      if (
        this.selectedGroup &&
        this.selectedGroup.name !== this.organizationGroup.name
      ) {
        const groupFound = this.surveysStore.getGroups.find(
          (g) => g.name === this.selectedGroup.name
        )
        if (!groupFound) {
          this.onSelectGroup(this.groupsForSelector[0])
        }
      }

      /*
       * Select a group instead of full organization if survey is configured to do so
       * Friskkollen move to individual users group
       */
      const groups = this.groupsForSelector
      const details = this.surveyDetails.details
      const userIsPulsAdmin = this.userStore.userIsPulsAdmin
      const userIsGroupAdmin = this.userStore.userIsGroupAdmin
      const organizationGroupIsSelected =
        this.selectedGroup.name === this.organizationGroup.name
      const groupFound = determineGroupToLoadFirst({
        groups,
        details,
        userIsGroupAdmin,
        userIsPulsAdmin
      })
      if (groupFound && organizationGroupIsSelected) {
        this.onSelectGroup(groupFound)
      }
    },
    filterRequestData({ data, requestFilter }) {
      if (requestFilter) {
        const subRequests = Object.keys(requestFilter)
        if (subRequests.length) {
          return subRequests.reduce((res, subRequestName) => {
            if (data && data[subRequestName]) {
              res[subRequestName] = { ...data[subRequestName] }
            } else {
              // we don't have any results for this item, but we may still
              // need messages from survey details
              const category = this.surveyDetails.details.categories.find(
                (c) => c.name === subRequestName
              )
              if (category) {
                res[subRequestName] = {
                  messages: category.details.messages
                }
              }
            }
            return res
          }, {})
        }
      }
      return data
    },
    getDataForRequestFromStores({ requestName, request }) {
      const storeName = requestNameToStoreMap[requestName]
      let store = this[storeName]

      return {
        data: this.filterRequestData({
          data: store[requestName],
          requestFilter: request.filter
        }),
        loading: store.loading[requestName],
        error: store.error[requestName]
      }
    },
    goToResults() {
      this.$router.push({
        name: 'Results'
      })
    },
    handleChangeViewMode(viewMode) {
      this.widgetStore.resetWidgetSliderStates()
      this.statesStore.setViewMode(viewMode)
    },
    handleToggleNotes() {
      this.showNotes = !this.showNotes
    },
    handleComponentError({ error, id, info }) {
      console.error(error, id, info)
    },
    handleWidgetDeregistration({ id, info }) {
      const infoCopy = { ...info }
      this.widgetStore.deregisterWidgetAction({
        id,
        info: infoCopy
      })
    },
    handleWidgetRegistration({
      id,
      information,
      data,
      doneWithAllWidgets = false
    }) {
      this.doneWithAllWidgets = doneWithAllWidgets
      this.widgetStore.registerWidgetAction({
        groupName: this.selectedGroup.name,
        id,
        information,
        data
      })
    },
    handleSurveyDetailsLoaded(surveyDetails) {
      if (this.shouldFindSuitableSurvey) {
        this.findSuitableSurvey()
      } else {
        this.findSuitableTimeWindowAndGroupForCurrentSurvey()
      }

      const hasDisabledAggregation = this.$route.query?.aggregate === 'false'
      const hasAggregateConfig = !!surveyDetails.details?.aggregate
      const surveyIsAggregate = hasAggregateConfig && !hasDisabledAggregation
      const surveyIsAggregateV2 =
        hasAggregateConfig &&
        !surveyDetails?.details.aggregate.config.containerSurvey &&
        surveyDetails.details.status !== 'aggregate'
      this.statesStore.setSurveyIsAggregate(surveyIsAggregate)
      this.statesStore.setSurveyIsAggregateV2(surveyIsAggregateV2)
      this.hasInitializedSurveyDetails = true
    },
    loadSurveyDetails(surveyName) {
      const currentYear = new Date().getFullYear() % 100
      this.surveysStore.loadSurveyDetailsAction({
        surveyName,
        currentSurveysOnly: `${currentYear - 1}-${currentYear}`
      })
    },
    navigateToSuitableSurvey() {
      const suitableSurvey = this.surveysStore.findSuitableSurveyInStore({
        surveyDetails: this.surveyDetails
      })
      if (suitableSurvey) {
        // change current survey to the suitable one
        this.$router.push({
          name: tabTypes.RESULTS,
          params: { surveyName: suitableSurvey.name }
        })
      } else {
        this.removeLoadQueryParam()
        this.suitableSurveyLookedUp = true
        this.findSuitableTimeWindowAndGroupForCurrentSurvey()
      }
    },
    onSelectSurvey(survey) {
      // use vue router to load survey with survey.name
      this.$router.push({
        name: tabTypes.RESULTS,
        params: { surveyName: survey.name }
      })
    },
    onSelectGroup(group) {
      if (group.name !== this.selectedGroup.name) {
        if (this.showNotes) {
          this.showNotes = false
        }
        if (group.name !== this.organizationName) {
          this.teamsStore.getTeamForGroupAction({
            groupName: group.name
          })
        } else {
          this.statesStore.setTeamForSelectedGroup(null)
        }
        this.insightsStore.clear()
        this.notesStore.clear()
        this.actionsStore.clear()
        this.resultsStore.clear()
        this.responsesStore.$reset()
        this.statesStore.setSelectedGroup(group)
        this.widgetStore.clear()
        this.realtimeStore.clear()
        this.requestDataForRegisteredWidgets()
        if (
          this.selectedTab === tabTypes.FREETEXT ||
          this.selectedTab === tabTypes.EXPORT
        ) {
          {
            // if user is admin that has anything else than RESULTS or COMPARISON tabs selected
            // and user is not admin of current group
            // then switch tab to look at RESULTS
            if (
              !this.userStore.userIsPulsAdmin &&
              this.userStore.userIsGroupAdmin &&
              !this.userIsAdminForCurrentGroup
            ) {
              this.onTabSelected(tabTypes.RESULTS)
            }
          }
        }
      }
    },
    onSelectAttributeSegment(selectedAttributeSegment) {
      this.actionsStore.clear()
      // this.insightsStore.clear()
      this.widgetStore.resetWidgetDeregistrationsAction()
      this.realtimeStore.clear()
      this.resultsStore.clearQuestionResponses()
      this.resultsStore.clearResponsesPerPerson()
      this.resultsStore.clearCurrentlyLoadedSurveys()
      this.responsesStore.$reset()

      if (selectedAttributeSegment.name === 'all') {
        this.statesStore.setSelectedAttributeSegment(null)
      } else {
        const [attributeName, segmentName] =
          selectedAttributeSegment.name.split('-')
        const segmentTitle = selectedAttributeSegment.segmentTitle
        const attributeTitle = selectedAttributeSegment.attributeTitle
        const attributeSegment = {
          name: `${attributeName}-${segmentName}`,
          attributeName,
          segmentName,
          segmentTitle,
          attributeTitle,
          title: selectedAttributeSegment.title
        }
        this.statesStore.setSelectedAttributeSegment(attributeSegment)
      }
      // this.resultsStore.updateResults({ surveyName: this.surveyName }) // needed?
      if (this.selectedTab === tabTypes.RESULTS) {
        this.requestDataForRegisteredWidgets()
      }
    },
    onTabSelected(tabName) {
      if (tabName !== this.selectedTab) {
        if (tabName === tabTypes.COMPARISON) {
          this.comparisonStore.clear()
          this.realtimeStore.clear()
          this.widgetStore.clear()
          this.resultsStore.clear()
          this.responsesStore.$reset()
        }
        if (tabName === tabTypes.RESULTS) {
          this.widgetStore.clear()
          this.resultsStore.clear()
          this.realtimeStore.clear()
        }

        this.selectedTab = tabName
        this.$router.push({
          name: tabName,
          params: { surveyName: this.surveyName }
        })
      }
    },
    onSurveyLoad() {
      this.hasLoadedOlderSurveys = false
      this.actionsStore.$reset()
      this.resultsStore.$reset()
      this.responsesStore.$reset()
      this.surveysStore.clear()
      this.insightsStore.clear()
      this.widgetStore.$reset()
      this.realtimeStore.clear()
      this.statesStore.setSurveyName(this.surveyName)
      this.loadSurveyDetails(this.surveyName)
    },
    requestDataForRegisteredWidgets() {
      const requestedData = {}
      const groupName = this.widgetRegistrations.groupName
      const widgetNames = Object.keys(this.widgetRegistrations?.widgets || {})
      widgetNames.forEach((widgetName) => {
        const widget = this.widgetRegistrations.widgets[widgetName]
        if (widget && widget.requests) {
          Object.keys(widget.requests).forEach((requestName) => {
            if (!this.requestCallbacks[requestName]) {
              const dataName = storeDataKeyToRequestTypeMap[requestName]
              // also merge requestedData with existing data for dataName in requestedData
              const params = {
                ...requestedData[dataName]?.requestData.params,
                ...widget.requests[dataName]?.params
              }
              requestedData[dataName] = {
                data: {
                  ...requestedData[dataName]?.data,
                  ...widget.data
                },
                testMode: widget.testMode,
                requestData: {
                  ...requestedData[dataName]?.requestData,
                  params
                }
              }
            }
          })
        }
      })
      Object.keys(requestedData).forEach((dataName) => {
        makeDataRequest({
          dataName,
          widgetData: requestedData[dataName].data,
          requestData: requestedData[dataName].requestData,
          groupName
        })
      })
    },
    removeLoadQueryParam() {
      this.$router.push({
        query: Object.fromEntries(
          Object.entries(this.$route.query).filter(([key]) => key !== 'load')
        )
      })
    },
    loadAllHistoricalSurveys() {
      this.hasLoadedOlderSurveys = true
      this.surveysStore.loadSurveyDetailsAction({
        surveyName: this.surveyName,
        currentSurveysOnly: false
      })
    },
    setSelectedTimePeriod(timePeriod) {
      if (timePeriod) {
        // todo: determine if time period starts in a year where we are missing history
        const timeObjectForTimePeriod = timeObjectFromSelectedTimePeriod({
          selectedTimePeriod: timePeriod
        })
        const year =
          subtractTimeObjectFromSurveyDate({
            surveyDate: this.surveyDetails.details.start_date,
            timeObject: timeObjectForTimePeriod
          }).getFullYear() % 100
        if (year < this.historyLoadedUntilYear) {
          this.surveysStore.loadSurveyDetailsAction({
            surveyName: this.surveyName,
            currentSurveysOnly: false
          })
        }
        this.widgetStore.reRegisterWidgetsAction()
        this.realtimeStore.clear()
        this.statesStore.setSelectedTimePeriodAction(timePeriod)
        if (this.selectedTab === tabTypes.RESULTS) {
          this.requestDataForRegisteredWidgets()
        }
      }
    },
    updateWidgetSliderState({ id, index, info }) {
      const infoCopy = { ...info }
      this.widgetStore.updateWidgetSliderAction({
        id,
        index,
        info: infoCopy
      })
    },
    // { categoryName, params }
    updateResponses({ responses, confirm = false }) {
      const surveyFinishedParams = confirm
        ? {
            confirm,
            surveyTitle: this.surveyTitle,
            surveyEndDate: this.surveyDetails.details.end_date,
            locale: localStorage.selectedLocale || 'en',
            usersName: this.userInfo.fullName,
            variant: this.surveyDetails.details.config.variant || 'feelgood'
          }
        : {}

      if (confirm) {
        this.responsesStore.confirmUserResponsesAction({
          surveyName: this.surveyName,
          responses,
          surveyFinishedParams
        })
      } else {
        this.responsesStore.updateUserResponsesAction({
          surveyName: this.surveyName,
          responses,
          surveyFinishedParams
        })
      }
    },
    updateWidgetRegistration({ id, requests, mode }) {
      this.widgetStore.updateWidgetRegistrationAction({
        groupName: this.selectedGroup.name,
        id,
        requests,
        mode
      })
    },
    handleProposalSelected(data) {
      // Forward the proposal-selected event from WidgetGrid to ResultsMessages component
      if (this.$refs.resultsMessages) {
        this.$refs.resultsMessages.handleProposalFromWidget(data)
      }
    }
  }
}
</script>
